import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import merge from "lodash/merge";

export default class CommunicationSettings {
  /**
   * @param {Object} settings Communication settings data (loaded from API)
   */
  constructor(settings) {
    this.originalSettings = settings;
    this.newSettings = cloneDeep(settings);
    if (settings.parent_settings) {
      this.parentSettings = new CommunicationSettings(settings.parent_settings);
    }
    this.changes = [];
  }

  /**
   * Returns the latest category settings.
   *
   * @returns {Array<Object>}
   */
  get categories() {
    return this.newSettings.categories;
  }

  /**
   * Returns the latest communication settings.
   *
   * @returns {Array<Object>}
   */
  get communications() {
    return this.newSettings.communications;
  }

  /**
   * Returns the latest communication settings matching the category passed.
   *
   * @param {String} category
   *
   * @returns {Array<Object>}
   */
  findCommunications(category) {
    if (category === "all") return this.communications;

    return this.communications.filter(
      (setting) => setting.category === category
    );
  }

  /**
   * Returns if the settings have been updated.
   *
   * @returns {Boolean}
   */
  get isUpdated() {
    return this.changes.length > 0;
  }

  /**
   * Updates the settings with the setting passed.
   *
   * @param {Object} newSetting The setting you are updating
   * @param {String} newSetting.category
   * @param {String|null} newSetting.key
   * @param {Boolean} newSetting.enabled
   * @param {Boolean} newSetting.email
   * @param {Boolean} newSetting.sms
   * @param {Boolean} newSetting.push
   */
  update(newSetting) {
    const changeIndex = this._changeIndex(newSetting);
    if (changeIndex >= 0) {
      if (this._isOriginalValue(newSetting)) {
        this.changes.splice(changeIndex, 1);
      } else {
        this.changes[changeIndex] = newSetting;
      }
    } else {
      this.changes.push(newSetting);
    }
    this._updateSettings(newSetting);
  }

  /**
   * Finds the latest setting matching the category and key passed
   *
   * @param {Object} params
   * @param {String} params.category The category of the setting to return
   * @param {String|null} params.key The key of the setting to return (null for category settings)
   * @returns {Object|undefined} The setting matching the category and key or undefined
   *   if not found.
   */
  findSetting({ category, key = null }) {
    return this._findSetting({
      category,
      key,
      settings: this.newSettings
    });
  }

  /**
   * Finds the original setting matching the category and key passed
   *
   * @param {Object} params
   * @param {String} params.category The category of the setting to return
   * @param {String|null} params.key The key of the setting to return (null for category settings)
   * @returns {Object|undefined} The setting matching the category and key or undefined
   *   if not found.
   */
  findOriginalSetting({ category, key = null }) {
    return this._findSetting({
      category,
      key,
      settings: this.originalSettings
    });
  }

  /**
   * Checks if the setting matching the category, key and format is disabled based on
   * higher level settings.
   *
   * @param {Object} params
   * @param {String} params.category The category of the setting to check
   * @param {String|null} params.key The key of the setting to check (null for category settings)
   * @param {String} params.format The format of the setting to check (all for top level settings)
   * @returns {Boolean} Whether the setting is disabled
   */
  isDisabled({ category, key = null, format }) {
    if (
      this.parentSettings?.displayValue({ category, key, format }) === false
    ) {
      return true;
    }

    const setting = this.findSetting({ category, key });
    if (setting.required) return true;

    let globalSetting;
    let categorySetting;

    if (key) {
      globalSetting = this.findSetting({ category: "all" });
      categorySetting = this.findSetting({ category });
    } else if (category !== "all") {
      globalSetting = this.findSetting({ category: "all" });
    }

    if (format === "all") {
      if (categorySetting) {
        if (!categorySetting.enabled) return true;
      }
      if (globalSetting) {
        if (!globalSetting.enabled) return true;
      }
      return false;
    } else {
      if (categorySetting) {
        if (!categorySetting.enabled) return true;
        if (!categorySetting[format]) return true;
      }
      if (globalSetting) {
        if (!globalSetting.enabled) return true;
        if (!globalSetting[format]) return true;
      }
      // Category settings provide the ability to toggle off all formats
      // at once in the UI (enabled). Individual communication settings do not.
      if (!key && !setting.enabled) return true;

      return false;
    }
  }

  /**
   * Returns if any settings are disabled for the category passed.
   *
   * @param {String} Category to search for disabled settings
   *
   * @returns [Boolean]
   */
  anyDisabled(category) {
    const categorySetting = this.findSetting({ category });
    const filterCheck = (setting) => {
      return setting.category === "all" || setting.category === category;
    };
    const disabledCheck = (setting) => {
      if (!setting.enabled) return true;

      return categorySetting.supported_formats.some((format) => {
        return !setting[format];
      });
    };

    const categorySettings = this.categories.filter(filterCheck);
    const communicationSettings = this.communications.filter(filterCheck);
    return (
      categorySettings.some(disabledCheck) ||
      communicationSettings.some(disabledCheck)
    );
  }

  /**
   * Returns the display value for the setting matching the category, key and format. This will
   * return the value of the setting or false if disabled.
   *
   * @param {Object} params
   * @param {String} params.category The category of the setting to check
   * @param {String|null} params.key The key of the setting to check (null for category settings)
   * @param {String} params.format The format of the setting to check (all for top level settings)
   * @returns {Boolean} The display value for the setting
   */
  displayValue({ category, key = null, format }) {
    const setting = this.findSetting({ category, key });
    if (setting.required) return true;

    if (this.isDisabled({ category, key, format })) return false;

    if (format === "all") {
      return setting.enabled;
    } else {
      return setting[format];
    }
  }

  // private

  _findSetting({ category, key, settings }) {
    if (key) {
      return settings.communications.find((value) => {
        return value.category === category && value.key === key;
      });
    } else {
      return settings.categories.find((value) => {
        return value.category === category && value.key === key;
      });
    }
  }

  _changeIndex(setting) {
    return this.changes.findIndex((value) => {
      return value.category === setting.category && value.key === setting.key;
    });
  }

  _isOriginalValue(setting) {
    const originalSetting = this.findOriginalSetting({
      category: setting.category,
      key: setting.key
    });

    const newSetting = {
      ...originalSetting,
      ...setting
    };

    return isEqual(newSetting, originalSetting);
  }

  _updateSettings(newSetting) {
    const currentSetting = this.findSetting({
      category: newSetting.category,
      key: newSetting.key
    });
    merge(currentSetting, newSetting);
  }
}
